/**
 * "Are these two names the same instrument?" The ONE rule (docgen E1, 2026-10-05).
 *
 * A questionnaire is written both ways in a protocol: "Columbia-Suicide Severity Rating Scale (C-SSRS)" in a
 * heading and "C-SSRS" in the schedule. Two names are the same instrument when
 *   - they are equal once spelling is folded (case, hyphens, spaces),
 *   - or one is an ACRONYM of the other: an acronym-like short name (two or more capitals, or a digit:
 *     "C-SSRS", "HADS", "PHQ-9") equals the acronym the other carries in its parentheses, or the initials of
 *     its significant words (hyphen-joined words count as words).
 * No named-instrument table: acronyms are derived from the names themselves. Short initials (under three
 * letters) and acronyms the other name only has by chance never match, so "Vital Signs" is not "VS".
 *
 * Shared by the wording matcher (schedule coverage, visit-form matching) and the CRF list de-duplication.
 * Pure. No Firestore, no model.
 */

const FILLER = new Set(["and", "of", "the", "for", "in", "with", "to", "a", "an", "on"]);
/** Words an instrument's name may end with that its acronym leaves out ("... Scale questionnaire"). */
const TRAILING_GENERIC = new Set(["scale", "questionnaire", "inventory", "form", "assessment", "index", "test", "instrument", "checklist", "measure", "survey", "rating"]);

const squash = (s: string): string => s.normalize("NFKC").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
const withoutParens = (s: string): string => s.replace(/\([^)]*\)/g, " ").replace(/\s+/g, " ").trim();

/** Capitals-or-digit shape of an acronym as written: "C-SSRS", "PHQ-9", "UAS7", "MoCA". "urine", "local" are not. */
function looksLikeAcronym(raw: string): boolean {
  const caps = (raw.match(/\p{Lu}/gu) ?? []).length;
  return caps >= 2 || (/\d/.test(raw) && /\p{L}/u.test(raw));
}

function parentheticalAcronyms(name: string): string[] {
  const out: string[] = [];
  for (const m of name.matchAll(/\(([^)]*)\)/g)) {
    for (const part of m[1].split(/\s*(?:,|;|\/|\band\b)\s*/i)) {
      const k = squash(part);
      if (looksLikeAcronym(part) && k.length >= 3 && !out.includes(k)) out.push(k);
    }
  }
  return out;
}

const wordsOf = (s: string): string[] => s.split(/[\s\-‐-―/]+/).filter(Boolean);

/** What the full name's initials spell, with and without its trailing generic words. */
function initialsKeys(name: string): string[] {
  const keys: string[] = [];
  let ws = wordsOf(withoutParens(name)).filter((w) => !FILLER.has(w.toLowerCase()));
  for (;;) {
    if (ws.length >= 3) {
      const k = squash(ws.map((w) => Array.from(w)[0] ?? "").join(""));
      if (k.length >= 3 && !keys.includes(k)) keys.push(k);
    }
    if (ws.length > 3 && TRAILING_GENERIC.has(ws[ws.length - 1].toLowerCase())) ws = ws.slice(0, -1);
    else break;
  }
  return keys;
}

/** The acronym a name IS (once trailing generic words like "Scale" are dropped), or null when it is a longer name. */
function bareAcronym(name: string): string | null {
  const ws = withoutParens(name).split(/\s+/).filter(Boolean);
  while (ws.length > 1 && TRAILING_GENERIC.has(ws[ws.length - 1].toLowerCase())) ws.pop();
  return ws.length === 1 && looksLikeAcronym(ws[0]) && squash(ws[0]).length >= 3 ? squash(ws[0]) : null;
}

/**
 * One name IS the acronym of the other: the bare acronym ("C-SSRS", "HADS", "BPI Form") against a full name that
 * carries it in parentheses or spells it with its initials. Two FULL names that both carry the same acronym
 * ("Serious Adverse Event (SAE) Report" and "(SAE) Follow-up", "Brief Pain Inventory (BPI)" and "Brief
 * Psychiatric Inventory (BPI)") are different forms and never match. Spelling-equal names are NOT this.
 */
export function isAcronymOf(a: string, b: string): boolean {
  const x = String(a ?? "").trim();
  const y = String(b ?? "").trim();
  if (!x || !y) return false;
  const matches = (bare: string | null, full: string) => !!bare && (parentheticalAcronyms(full).includes(bare) || initialsKeys(full).includes(bare));
  return matches(bareAcronym(x), y) || matches(bareAcronym(y), x);
}

export function sameInstrumentName(a: string, b: string): boolean {
  const x = String(a ?? "").trim();
  const y = String(b ?? "").trim();
  if (!x || !y) return false;
  const sx = squash(withoutParens(x));
  const sy = squash(withoutParens(y));
  if (sx.length >= 3 && sx === sy) return true;
  return isAcronymOf(x, y);
}

/** A list with the later spelling of an already-present instrument dropped (the first is kept). */
export function dedupeBySameInstrument<T>(items: T[], nameOf: (item: T) => string): T[] {
  const kept: T[] = [];
  for (const item of items ?? []) {
    const n = nameOf(item);
    if (kept.some((k) => sameInstrumentName(nameOf(k), n))) continue;
    kept.push(item);
  }
  return kept;
}
